import { hydrateFreeBetMechanic } from "helpers/sportsbookRewardMechanics";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import {
  GameItem,
  mergeGameItem,
  updateGame,
} from "../../gameCenter/games/slice";
import sanitizeFileName from "helpers/sanitizeFileName";
import {
  findBonusTypeTitle,
  hydrateBonusScheduleFields,
  serializeBonusTypeData,
} from "helpers/bonusScheduleTypeData";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface BonusItem {
  id?: number;
  canGuestShow?: boolean;
  description: string;
  title: string;
  status: string;
  wagerStatus?: string;
  type: string;
  image: string;
  wagerX: number;
  payoutX: number;
  currencies: string[];
  [key: string]: any;
}
export interface BonusItemPayload {
  id?: number;
  canGuestShow: boolean;
  description: string;
  title: string;
  image: string | File;
  mobileImage: string | File;
  portraitImage: string | File;
  wagerX: number;
  payoutX: number;
  currencies: string[];
  typeId: number;
  typeData?: string;
  recurringCadence?: string;
  recurringGrantTime?: string;
  recurringWeekday?: string;
  recurringDayOfMonth?: string;
  reloadStartWeekday?: string;
  reloadStartTime?: string;
  reloadEndWeekday?: string;
  reloadEndTime?: string;
  statusId: number;
  wagerGamesListIds: number[];
  wagerBalancePriority: "any" | "real" | "bonus";
  label: string;
  isRealWagering: boolean;
  isBonusWagering: boolean;
  bankrollMode: "separately" | "together";
  priorityBankroll: "real" | "bonus" | null;
  payoutDestination: "real" | "bonus" | null;
  payoutCurrency: string | null;
  segmentIds?: number[];
  excludedSegmentIds?: number[];
  startedAt: string;
  finishedAt: string;
  showPreviousAt: string;
  claimPeriodInHours: number;
  activationPeriodInHours: number;
  wageringPeriodInHours: number;
  category?: string;
  additionalConfigs: {
    currency: string;
    betsRangeMin: number;
    betsRangeMax: number;
    activations: {
      type?: string;
      min?: string;
      max?: string;
      currency?: string;
      numberOfDeposit?: string;
    }[];
    rewards: {
      type?: string;
      value?: string;
      points?: string;
      gameId?: string;
      providerId?: string;
      providerTitle?: string;
      providerSlug?: string;
      gameUuid?: string;
      gameTitle?: string;
      gameImage?: string;
      totalAmount?: string;
      spinAmount?: string;
      max?: string;
      maxWin?: string;
      count?: string;
      currency?: string;
      templateId?: string;
      providerTemplate?: Record<string, unknown>;
      legacyTemplateId?: string;
      persistedConfig?: Record<string, any>;
      amount?: string;
      winningsDestination?: "real" | "bonus";
    }[];
    pendingRewardDeletes?: string[];
  }[];
}

type BonusBankrollPolicySource = Pick<
  BonusItemPayload,
  | "bankrollMode"
  | "priorityBankroll"
  | "payoutDestination"
  | "isRealWagering"
  | "isBonusWagering"
>;

export const normalizeBonusBalanceRewardValue = (
  value: unknown,
): string | undefined => {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

export const normalizeBonusBankrollPolicy = (
  source: BonusBankrollPolicySource,
): Pick<
  BonusItemPayload,
  "bankrollMode" | "priorityBankroll" | "payoutDestination"
> => {
  const allowedBankrolls: ("real" | "bonus")[] = [];
  if (source.isRealWagering) allowedBankrolls.push("real");
  if (source.isBonusWagering) allowedBankrolls.push("bonus");

  const fallback = allowedBankrolls[0] ?? null;
  const bankrollMode =
    source.bankrollMode === "together" && fallback ? "together" : "separately";

  return {
    bankrollMode,
    priorityBankroll:
      source.priorityBankroll &&
      allowedBankrolls.includes(source.priorityBankroll)
        ? source.priorityBankroll
        : fallback,
    payoutDestination:
      source.payoutDestination &&
      allowedBankrolls.includes(source.payoutDestination)
        ? source.payoutDestination
        : fallback,
  };
};
export interface BonusesBunchPayload {
  ids: number[];
  statusId: string;
}
export interface BonusTranslationPayload {
  locale: string;
  title: string;
  description?: string;
  isActive: number | boolean;
  content: string;
  image?: string | File;
  mobileImage?: string | File;
  portraitImage?: string | File;
}

export interface BonusTranslationState {
  [locale: string]: BonusTranslationPayload;
}
interface SetRulesPayload {
  id: string;
  currency: string;
  type?: string;
  min?: string;
  max?: string;
  numberOfDeposit?: string;
  cancelAmount?: string;
  depositRequired?: number;
}
interface SetRewardsPayload {
  id: string;
  type?: string;
  value?: string;
  points?: string;
  gameId?: string;
  gameUuid?: string;
  totalAmount?: string;
  spinAmount?: string;
  max?: string;
  maxWin?: string;
  count?: string;
  currency?: string;
  depositPercentage?: string;
  depositPercentageMax?: string;
  depositStatic?: string;
  coins?: string;
  loyalty?: string;
  ranking?: string;
  freespins?: {
    gameId?: string;
    providerId?: string;
    gameUuid?: string;
    totalAmount?: string;
    spinAmount?: string;
    maxWin?: string;
    winningsDestination?: "real" | "bonus";
  };
  freebet?: {
    provider: string;
    providerTemplate?: Record<string, unknown>;
    templateId?: number;
    amount?: number;
    currency?: string;
    maxWin?: number | string;
    winningsDestination?: "real" | "bonus";
  };
  rewards?: {
    type: string;
    config?: string;
  }[];
}
interface SetBetssPayload {
  id: string;
  currency: string;
  betsRangeMin: number;
  betsRangeMax: number;
}

interface BonusesState {
  bonusesList: BonusItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  selectedItemGames: number[];
  gamesList: GameItem[];
  typesList: {
    id: number;
    title: string;
  }[];
  statusList: {
    id: number;
    title: string;
  }[];
  wagerList: {
    id: number;
    title: string;
  }[];
  activePageGames: number;
  totalPagesGames: number;
  totalItemsGames: number;
  isListGamesFetched: boolean;
  isListTypesFetched: boolean;
  isListStatusFetched: boolean;
  isListWagerFetched: boolean;
  payload: BonusItemPayload;
  translations: BonusTranslationState;
  isItemFetching: boolean;
  processingItemId?: BonusItem["id"];
  processingGameItemId?: GameItem["id"];
  spinsOptions: { value: string; label: string }[];
  isSpinsLoading: boolean;
  isSpinsError: boolean;
}
interface GetListPayload {
  limit: number;
  offset: number;
  statusId: number;
  typeId: number;
  currencyIds: string[];
  title: string;
  order: {
    title: string;
    id: string;
    type: string;
    status: string;
  };
}
interface GetGamesListPayload {
  limit: number;
  offset: number;
  providerIds?: number[];
  gameTitle?: string;
  order: {
    title?: string;
    ptoviderTitle?: string;
  };
}
interface UpdateFreeSpinsGamesPayload {
  gameIds: number[];
  isActive: number;
}

export const setBonusTranslation = createAsyncThunk<
  { locale: string; data: BonusTranslationPayload },
  { bonusId: number; payload: BonusTranslationPayload },
  { rejectValue: ApiErrorPayload }
>(
  "bonus/set-translation",
  async ({ bonusId, payload }, { rejectWithValue }) => {
    try {
      const data = new FormData();

      data.append("locale", payload.locale ?? "");
      data.append("title", payload.title ?? "");
      data.append("description", payload.description ?? "");
      data.append("content", payload.content ?? "");
      data.append("isActive", payload.isActive ? "1" : "0");

      if (payload.image instanceof File) {
        const safeName = sanitizeFileName(payload.image.name);

        data.append(
          "image",
          new File([payload.image], safeName, { type: payload.image.type }),
        );
      }
      if (payload.mobileImage instanceof File) {
        const safeName = sanitizeFileName(payload.mobileImage.name);

        data.append(
          "mobileImage",
          new File([payload.mobileImage], safeName, {
            type: payload.mobileImage.type,
          }),
        );
      }
      if (payload.portraitImage instanceof File) {
        const safeName = sanitizeFileName(payload.portraitImage.name);

        data.append(
          "portraitImage",
          new File([payload.portraitImage], safeName, {
            type: payload.portraitImage.type,
          }),
        );
      }

      await instance.put(
        `${apiRoutes.bonusSetTranslations}/${bonusId}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return { locale: payload.locale, data: payload };
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const getBonusTranslation = createAsyncThunk(
  "bonus/get-translation",
  async ({ bonusId, locale }: { bonusId: number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.bonusGetTranslations}/${bonusId}?locale=${locale}`,
      );
      return { locale, data: result.data };
    } catch (error) {
      throw error;
    }
  },
);

const appendBonusTypeData = (
  data: FormData,
  state: {
    bonuses: {
      typesList: { id: number; title: string }[];
    };
  },
  payload: BonusItemPayload,
) => {
  const typeData = serializeBonusTypeData(
    findBonusTypeTitle(state.bonuses.typesList, payload.typeId),
    payload,
  );
  if (typeData) data.append("typeData", typeData);
};

export const createBonus = createAsyncThunk<
  number,
  BonusItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "bonus/create",
  async (payload: BonusItemPayload, { getState, rejectWithValue }) => {
    const state: any = getState();
    const statusList = state.bonuses.statusList;

    const defaultStatus = statusList.find((el: any) => el.title === "new");

    const normalizedStatusId =
      payload.statusId && payload.statusId !== 0
        ? payload.statusId
        : (defaultStatus?.id ?? 0);
    const bankrollPolicy = normalizeBonusBankrollPolicy(payload);

    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    if (payload.mobileImage instanceof File) {
      const safeName = sanitizeFileName(payload.mobileImage.name);

      data.append(
        "mobileImage",
        new File([payload.mobileImage], safeName, {
          type: payload.mobileImage.type,
        }),
      );
    }
    if (payload.portraitImage instanceof File) {
      const safeName = sanitizeFileName(payload.portraitImage.name);

      data.append(
        "portraitImage",
        new File([payload.portraitImage], safeName, {
          type: payload.portraitImage.type,
        }),
      );
    }

    data.append("title", (payload.title as string) ?? "");
    data.append("description", (payload.description as string) ?? "");
    data.append("wagerX", payload.wagerX.toString());
    data.append("payoutX", payload.payoutX.toString());
    data.append("typeId", payload.typeId.toString());
    data.append("canGuestShow", payload.canGuestShow.toString());

    data.append("statusId", normalizedStatusId.toString());

    if (payload.wagerGamesListIds.length) {
      payload.wagerGamesListIds.forEach((id) => {
        data.append("wagerGamesListIds[]", id.toString());
      });
    } else {
      data.append("wagerGamesListIds", "[]");
    }
    data.append("label", payload.label ?? "");
    data.append("wagerBalancePriority", payload.wagerBalancePriority);
    data.append("isRealWagering", payload.isRealWagering ? "1" : "0");
    data.append("isBonusWagering", payload.isBonusWagering ? "1" : "0");
    data.append("bankrollMode", bankrollPolicy.bankrollMode);
    if (bankrollPolicy.priorityBankroll) {
      data.append("priorityBankroll", bankrollPolicy.priorityBankroll);
    }
    if (bankrollPolicy.payoutDestination) {
      data.append("payoutDestination", bankrollPolicy.payoutDestination);
    }
    data.append("payoutCurrency", payload.payoutCurrency ?? "");

    if (payload.startedAt) data.append("startedAt", payload.startedAt);
    if (payload.finishedAt) data.append("finishedAt", payload.finishedAt);
    if (payload.showPreviousAt)
      data.append("showPreviousAt", payload.showPreviousAt);

    data.append(
      "wageringPeriodInHours",
      payload.wageringPeriodInHours.toString(),
    );
    data.append(
      "activationPeriodInHours",
      payload.activationPeriodInHours.toString(),
    );
    data.append("claimPeriodInHours", payload.claimPeriodInHours.toString());

    payload.currencies.forEach((currency) => {
      data.append("currencies[]", currency);
    });

    appendBonusTypeData(data, state, payload);
    if (payload.segmentIds?.length) {
      payload.segmentIds.forEach((id) => {
        data.append("segmentIds[]", id.toString());
      });
    } else {
      data.append("segmentIds", "[]");
    }
    if (payload.excludedSegmentIds?.length) {
      payload.excludedSegmentIds.forEach((id) => {
        data.append("excludedSegmentIds[]", id.toString());
      });
    } else {
      data.append("excludedSegmentIds", "[]");
    }

    if (payload.category) {
      data.append("category", payload.category ?? "all");
    }


    try {
       const response = await instance.post(`${apiRoutes.bonusCreate}`, data, {
         headers: { "Content-Type": "multipart/form-data" },
       });

       return response.data.id; 
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const setRules = createAsyncThunk<
  boolean,
  SetRulesPayload,
  { rejectValue: ApiErrorPayload }
>("bonus/setRules", async (payload, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.bonusSetRules}/${payload.id}`, {
      currency: payload.currency,
      depositRequired: payload.depositRequired,
      depositNumber: payload.numberOfDeposit,
      depositMin: payload.min,
      depositMax: payload.max,
      cancellationAmount: payload.cancelAmount,
    });

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const setRewards = createAsyncThunk<
  boolean,
  SetRewardsPayload,
  { rejectValue: ApiErrorPayload }
>("bonus/setRewards", async (payload, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.bonusSetRewards}/${payload.id}`, {
      currency: payload.currency,
      depositPercentage: payload.depositPercentage,
      depositPercentageMax: payload.depositPercentageMax,
      depositStatic: payload.depositStatic,
      coins: payload.coins,
      loyalty: payload.loyalty,
      ranking: payload.ranking,
      freespins: payload.freespins,
      freebet: payload.freebet,
      rewards: payload.rewards,
    });

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const setBets = createAsyncThunk<
  boolean,
  SetBetssPayload,
  { rejectValue: ApiErrorPayload }
>("bonus/setBets", async (payload, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.bonusSetBets}/${payload.id}`, {
      currency: payload.currency,
      min: payload.betsRangeMin,
      max: payload.betsRangeMax,
    });

    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateBonus = createAsyncThunk<
  boolean,
  BonusItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "bonus/update",
  async (payload: BonusItemPayload, { getState, rejectWithValue }) => {
  const data = new FormData();
  const bankrollPolicy = normalizeBonusBankrollPolicy(payload);

  if (payload.image instanceof File) {
    const safeName = sanitizeFileName(payload.image.name);

    data.append(
      "image",
      new File([payload.image], safeName, { type: payload.image.type }),
    );
  }
  if (payload.mobileImage instanceof File) {
    const safeName = sanitizeFileName(payload.mobileImage.name);

    data.append(
      "mobileImage",
      new File([payload.mobileImage], safeName, {
        type: payload.mobileImage.type,
      }),
    );
  }
  if (payload.portraitImage instanceof File) {
    const safeName = sanitizeFileName(payload.portraitImage.name);

    data.append(
      "portraitImage",
      new File([payload.portraitImage], safeName, {
        type: payload.portraitImage.type,
      }),
    );
  }
  data.append("title", (payload.title as string) ?? "");
  data.append("description", (payload.description as string) ?? "");
  data.append(
    "wagerX",
    payload.wagerX !== null ? payload.wagerX.toString() : "",
  );
  data.append(
    "payoutX",
    payload.payoutX !== null ? payload.payoutX.toString() : "",
  );
  data.append("typeId", payload.typeId.toString());
  data.append("canGuestShow", payload.canGuestShow.toString());
  data.append("statusId", payload.statusId.toString());
  if (payload.wagerGamesListIds.length) {
    payload.wagerGamesListIds.forEach((id) => {
      data.append("wagerGamesListIds[]", id.toString());
    });
  } else {
    data.append("wagerGamesListIds", "[]");
  }
  data.append("label", payload.label ?? "");
  data.append("wagerBalancePriority", payload.wagerBalancePriority);
  data.append("isRealWagering", payload.isRealWagering ? "1" : "0");
  data.append("isBonusWagering", payload.isBonusWagering ? "1" : "0");
  data.append("bankrollMode", bankrollPolicy.bankrollMode);
  if (bankrollPolicy.priorityBankroll) {
    data.append("priorityBankroll", bankrollPolicy.priorityBankroll);
  }
    if (bankrollPolicy.payoutDestination) {
      data.append("payoutDestination", bankrollPolicy.payoutDestination);
    }
    data.append("payoutCurrency", payload.payoutCurrency ?? "");
    if (payload.startedAt) data.append("startedAt", payload.startedAt);
  if (payload.finishedAt) data.append("finishedAt", payload.finishedAt);
  if (payload.showPreviousAt)
    data.append("showPreviousAt", payload.showPreviousAt);
  data.append(
    "wageringPeriodInHours",
    payload.wageringPeriodInHours.toString(),
  );
  data.append(
    "activationPeriodInHours",
    payload.activationPeriodInHours.toString(),
  );
  data.append("claimPeriodInHours", payload.claimPeriodInHours.toString());
  if (payload.currencies.length) {
    payload.currencies.forEach((currency) => {
      data.append("currencies[]", currency);
    });
  } else {
    data.append("currencies", "[]");
  }
  appendBonusTypeData(
    data,
    getState() as {
      bonuses: { typesList: { id: number; title: string }[] };
    },
    payload,
  );
  if (payload.segmentIds?.length) {
    payload.segmentIds.forEach((id) => {
      data.append("segmentIds[]", id.toString());
    });
  } else {
    data.append("segmentIds", "[]");
  }
  if (payload.excludedSegmentIds?.length) {
    payload.excludedSegmentIds.forEach((id) => {
      data.append("excludedSegmentIds[]", id.toString());
    });
  } else {
    data.append("excludedSegmentIds", "[]");
  }
  if (payload.category) {
    data.append("category", payload.category ?? "all");
  }
  try {
    await instance.put(`${apiRoutes.bonusUpdate}/${payload.id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const getBonusesList = createAsyncThunk(
  "bonuses/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.bonusesList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const updateBonusesBunch = createAsyncThunk(
  "bonuses/update-bunch",
  async (data: BonusesBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.bonusUpdateBunch}`, {
        ...data,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getBonusesItem = createAsyncThunk(
  "bonuses/item",
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.bonusItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getBonusTypesList = createAsyncThunk(
  "bonuses/types-list",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.bonusesTypesList}?limit=1000&offset=0`,
      );
      return result.data.items;
    } catch (error) {
      return undefined;
    }
  },
);
export const getBonusStatusList = createAsyncThunk(
  "bonuses/status-list",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.bonusesStatusList}?limit=1000&offset=0`,
      );
      return result.data.items;
    } catch (error) {
      return undefined;
    }
  },
);
export const getWagerList = createAsyncThunk("bonuses/wager-list", async () => {
  try {
    const result = await instance.get(
      `${apiRoutes.wagersList}?limit=30&offset=0`,
    );
    return result.data.items;
  } catch (error) {
    return undefined;
  }
});
export const getGamesList = createAsyncThunk(
  "bonuses/games/list",
  async (data: GetGamesListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.gamesList}`, {
        ...data,
        hasFreeSpins: 1,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const updateFreeSpinsGamesBunch = createAsyncThunk(
  "bonuses/games/update-bunch",
  async (data: UpdateFreeSpinsGamesPayload) => {
    try {
      await instance.put(`${apiRoutes.gameBunchUpdate}`, data);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getSpinsList = createAsyncThunk<
  { spinAmount: number }[],
  { gameId: string; currency: string },
  { rejectValue: ApiErrorPayload }
>("bonus/get-spins", async ({ gameId, currency }, { rejectWithValue }) => {
  try {
    const response = await instance.get(
      `${apiRoutes.gameSpinsList}?gameId=${gameId}&currency=${currency}`,
    );

    return response.data;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

const defPayload: BonusItemPayload = {
  title: "",
  description: "",
  typeId: 0,
  canGuestShow: true,
  wagerGamesListIds: [],
  wagerBalancePriority: "any",
  label: "",
  wageringPeriodInHours: 0,
  wagerX: 0,
  isRealWagering: true,
  isBonusWagering: true,
  bankrollMode: "separately",
  priorityBankroll: "real",
  payoutDestination: "real",
  payoutCurrency: null,
  image: "",
  mobileImage: "",
  portraitImage: "",
  statusId: 0,
  showPreviousAt: "",
  activationPeriodInHours: 0,
  startedAt: "",
  finishedAt: "",
  claimPeriodInHours: 0,
  recurringCadence: "",
  recurringGrantTime: "",
  recurringWeekday: "",
  recurringDayOfMonth: "",
  reloadStartWeekday: "",
  reloadStartTime: "",
  reloadEndWeekday: "",
  reloadEndTime: "",
  currencies: [],
  payoutX: 0,
  category: "all",
  segmentIds: [],
  excludedSegmentIds: [],
  additionalConfigs: [],
};

const initialState: BonusesState = {
  bonusesList: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  selectedItemGames: [],
  gamesList: [],
  isListGamesFetched: false,
  isItemFetched: false,
  isListTypesFetched: false,
  isListStatusFetched: false,
  isListWagerFetched: false,
  activePageGames: 1,
  totalItemsGames: 0,
  totalPagesGames: 0,
  typesList: [],
  statusList: [],
  wagerList: [],
  payload: defPayload,
  translations: {},
  isItemFetching: false,
  spinsOptions: [],
  isSpinsLoading: false,
  isSpinsError: false,
};

export const bonusesSlice = createSlice({
  name: "bonuses",
  initialState,
  reducers: {
    setTranslationLocally: (
      state: BonusesState,
      action: PayloadAction<{ locale: string; data: BonusTranslationPayload }>,
    ) => {
      const { locale, data } = action.payload;
      state.translations[locale] = data;
    },
    handleUpdateFields: (
      state: BonusesState,
      action: PayloadAction<Partial<BonusItemPayload>>,
    ) => {
      const updatedPayload = { ...state.payload, ...action.payload };
      state.payload = {
        ...updatedPayload,
        ...normalizeBonusBankrollPolicy(updatedPayload),
      };
    },
    handleClearFields: (state: BonusesState) => {
      state.payload = defPayload;
      state.isItemFetched = false;
    },
    handleClearActivitaionField: (
      state: BonusesState,
      action: PayloadAction<{ type: string; currency: string }>,
    ) => {
      const filteredActivation = state.payload?.additionalConfigs?.map((el) => {
        if (el?.currency === action.payload.currency) {
          return {
            ...el,
            activations: el.activations.filter(
              (item) => item.type !== action.payload.type,
            ),
          };
        } else {
          return {
            ...el,
          };
        }
      });
      state.payload = {
        ...state.payload,
        // @ts-ignore
        additionalConfigs: filteredActivation,
      };
    },
    handleRemoveReward: (
      state: BonusesState,
      action: PayloadAction<{ type: string; currency: string }>,
    ) => {
      const filteredConfigs = state.payload?.additionalConfigs?.map((el) => {
        if (el?.currency === action.payload.currency) {
          const removedReward = el.rewards.find(
            (item) => item.type === action.payload.type,
          );
          const pendingRewardDeletes =
            (action.payload.type === "comboBoost" &&
              removedReward?.templateId) ||
            action.payload.type === "bonusBalance"
              ? Array.from(
                  new Set([
                    ...(el.pendingRewardDeletes ?? []),
                    action.payload.type === "comboBoost"
                      ? "comboboost"
                      : "bonusBalance",
                  ]),
                )
              : el.pendingRewardDeletes;

          return {
            ...el,
            rewards: el.rewards.filter(
              (item) => item.type !== action.payload.type,
            ),
            pendingRewardDeletes,
          };
        } else {
          return {
            ...el,
          };
        }
      });
      state.payload = {
        ...state.payload,
        // @ts-ignore
        additionalConfigs: filteredConfigs,
      };
    },
    handlePaginate: (state: BonusesState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (state: BonusesState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: BonusesState) => {
      if (state.selectedItems.length === state.bonusesList.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.bonusesList.map((el) => el.id ?? 0);
      }
    },
    handlePaginateGames: (
      state: BonusesState,
      action: PayloadAction<number>,
    ) => {
      state.activePageGames = action.payload;
      state.isListGamesFetched = false;
      state.selectedItemGames = [];
    },
    handleSelectItemGames: (
      state: BonusesState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItemGames.includes(action.payload)) {
        state.selectedItemGames = state.selectedItemGames.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItemGames = [...state.selectedItemGames, action.payload];
      }
    },
    handleSelectAllGames: (state: BonusesState) => {
      if (state.selectedItemGames.length === state.gamesList.length) {
        state.selectedItemGames = [];
      } else {
        state.selectedItemGames = state.gamesList.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBonusesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.bonusesList = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getBonusTypesList.fulfilled, (state, action) => {
      state.isListTypesFetched = true;
      state.typesList = action.payload ?? [];
    });
    builder.addCase(getBonusStatusList.fulfilled, (state, action) => {
      state.isListStatusFetched = true;
      state.statusList = action.payload ?? [];
    });
    builder.addCase(getWagerList.fulfilled, (state, action) => {
      state.isListWagerFetched = true;
      state.wagerList = action.payload ?? [];
    });
    builder.addCase(getGamesList.fulfilled, (state, action) => {
      state.isListGamesFetched = true;
      state.gamesList = action.payload?.items ?? [];
      state.totalItemsGames = action.payload?.totalItems ?? 1;
      state.totalPagesGames = action.payload?.totalPages ?? 1;
    });
    builder.addCase(updateGame.pending, (state, action) => {
      state.processingGameItemId = action.meta.arg.id;
    });
    builder.addCase(updateGame.fulfilled, (state, action) => {
      state.gamesList = state.gamesList.map((game) =>
        game.id === action.meta.arg.id
          ? mergeGameItem(game, action.payload)
          : game,
      );
      state.processingGameItemId = undefined;
    });
    builder.addCase(updateGame.rejected, (state) => {
      state.processingGameItemId = undefined;
    });
    builder.addCase(updateFreeSpinsGamesBunch.fulfilled, (state, action) => {
      if (action.payload) {
        state.isListGamesFetched = false;
        state.selectedItemGames = [];
      }
    });
    builder.addCase(createBonus.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createBonus.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createBonus.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateBonus.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateBonus.fulfilled, (state, action) => {
      const updatedBonus = action.meta.arg;
      const updatedType = state.typesList.find(
        (item) => item.id === updatedBonus.typeId,
      )?.title;
      const updatedStatus = state.statusList.find(
        (item) => item.id === updatedBonus.statusId,
      )?.title;

      state.bonusesList = state.bonusesList.map((bonus) =>
        bonus.id === updatedBonus.id
          ? mergeExistingFields(bonus, {
              title: updatedBonus.title,
              description: updatedBonus.description,
              image:
                typeof updatedBonus.image === "string"
                  ? updatedBonus.image
                  : bonus.image,
              wagerX: updatedBonus.wagerX,
              payoutX: updatedBonus.payoutX,
              currencies: updatedBonus.currencies,
              type: updatedType ?? bonus.type,
              status: updatedStatus ?? bonus.status,
            })
          : bonus,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateBonus.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateBonusesBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getBonusesItem.fulfilled, (state, action) => {
      const transformBonusResponse = (data: any): BonusItemPayload => {
        const currenciesArray = Array.from(
          new Set([
            ...data.betRanges.map((b: any) => b.currency),
            ...data.activationRules
              .filter((a: any) => a.type !== "promoCode")
              .map((a: any) => a.currency),
            ...data.rewards.map((r: any) => r.currency),
          ]),
        );
        const transformedPayload: BonusItemPayload = {
          id: data.id,
          title: data.title,
          description: data.description,
          segmentIds: data.segmentIds ?? [],
          excludedSegmentIds: data.excludedSegmentIds ?? [],
          image: data.image,
          mobileImage: data.mobileImage,
          portraitImage: data.portraitImage,
          wagerX: data.wagerX,
          payoutX: data.payoutX,
          currencies: currenciesArray,
          typeId:
            state.typesList.filter((el) => el.title === data.type)[0]?.id ?? 0,
          canGuestShow: data.canGuestShow ?? true,
          statusId:
            state.statusList.filter((el) => el.title === data.status)[0]?.id ??
            0,
          wagerGamesListIds: data.wagerGamesListIds ?? [],
          wagerBalancePriority: data.wagerBalancePriority ?? "any",
          label: data.label ?? "",
          isRealWagering: data.isRealWagering,
          isBonusWagering: data.isBonusWagering,
          bankrollMode: data.bankrollMode ?? "separately",
          priorityBankroll: data.priorityBankroll ?? null,
          payoutDestination: data.payoutDestination ?? null,
          payoutCurrency: data.payoutCurrency ?? null,
          startedAt: data.startedAt,
          finishedAt: data.finishedAt,
          showPreviousAt: data.showPreviousAt,
          claimPeriodInHours: data.claimPeriodInHours,
          activationPeriodInHours: data.activationPeriodInHours,
          wageringPeriodInHours: data.wageringPeriodInHours,
          typeData: data.typeData,
          ...hydrateBonusScheduleFields(data.type, data.typeData),
          category: data.category ?? "all",

          additionalConfigs: currenciesArray.map((currency) => ({
            currency: currency,
            betsRangeMin: data.betRanges.filter(
              (el: any) => el.currency === currency,
            )[0]?.min,
            betsRangeMax: data.betRanges.filter(
              (el: any) => el.currency === currency,
            )[0]?.max,
            activations: data.activationRules
              .filter(
                (rule: any) =>
                  rule.currency === currency && rule.type !== "promoCode",
              )
              .map((rule: any) => ({
                type: rule.type,
                min: rule.config?.min,
                max: rule.config?.max,
                currency: rule.currency,
                numberOfDeposit: rule.config.numberOfDeposit,
              })),
            rewards: data.rewards
              .filter((reward: any) => reward.currency === currency)
              .map((reward: any) => {
                if (["freebet", "bet_refund", "snr"].includes(reward.type)) {
                  return {
                    ...hydrateFreeBetMechanic(
                      String(reward.config?.templateId),
                      reward.config?.providerTemplate,
                      reward.type,
                    ),
                    templateId: String(reward.config?.templateId),
                    providerTemplate: reward.config?.providerTemplate,
                    persistedConfig: reward.config,
                    amount: String(reward.config?.amount),
                    maxWin:
                      reward.config?.maxWin != null
                        ? String(reward.config.maxWin)
                        : "",
                    winningsDestination:
                      reward.config?.winningsDestination === "real"
                        ? "real"
                        : "bonus",
                    currency: reward.currency,
                  };
                }

                if (reward.type === "comboboost") {
                  return {
                    type: "comboBoost",
                    templateId: String(reward.config?.templateId),
                    providerTemplate: reward.config?.providerTemplate,
                    persistedConfig: reward.config,
                    currency: reward.currency,
                  };
                }

                return {
                  type: reward.type,
                  value:
                    reward.type === "bonusBalance"
                      ? normalizeBonusBalanceRewardValue(reward.config?.value)
                      : reward.config?.value,
                  points: reward.config?.points,
                  gameId: reward.config?.gameId,
                  providerId: reward.config?.providerId,
                  providerTitle: reward.config?.providerTitle,
                  providerSlug: reward.config?.providerSlug,
                  gameUuid: reward.config?.gameUuid,
                  gameTitle: reward.config?.gameTitle,
                  gameImage: reward.config?.gameImage,
                  totalAmount: reward.config?.totalAmount,
                  spinAmount: reward.config?.spinAmount,
                  max: reward.config?.max,
                  maxWin: reward.config?.maxWin,
                  winningsDestination:
                    reward.config?.winningsDestination === "real"
                      ? "real"
                      : "bonus",
                  count: reward.config?.totalAmount,
                };
              }),
          })),
        };
        return {
          ...transformedPayload,
          ...normalizeBonusBankrollPolicy(transformedPayload),
        };
      };
      state.isItemFetched = true;
      state.payload = transformBonusResponse(action.payload);
    });
    builder.addCase(getBonusTranslation.fulfilled, (state, action) => {
      const { locale, data } = action.payload;
      state.translations[locale] = {
        ...data,
        locale,
        image: data.image ?? "",
        mobileImage: data.mobileImage ?? "",
        portraitImage: data.portraitImage ?? "",
      };
    });
    builder.addCase(setBonusTranslation.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(setBonusTranslation.fulfilled, (state, action) => {
      const { locale, data } = action.payload;
      state.translations[locale] = data;
      state.isItemFetching = false;
    });
    builder.addCase(setBonusTranslation.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(setRules.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(setRules.fulfilled, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(setRules.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(getSpinsList.pending, (state) => {
      state.isSpinsLoading = true;
      state.isSpinsError = false; 
    });

    builder.addCase(getSpinsList.fulfilled, (state, action) => {
      state.isSpinsLoading = false;
      state.isSpinsError = false;

      state.spinsOptions = action.payload.map((el) => ({
        value: String(el.spinAmount),
        label: String(el.spinAmount),
      }));
    });

    builder.addCase(getSpinsList.rejected, (state) => {
      state.isSpinsLoading = false;
      state.isSpinsError = true; 
      state.spinsOptions = []; 
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handlePaginateGames,
  handleSelectAllGames,
  handleSelectItemGames,
  handleClearFields,
  handleUpdateFields,
  handleClearActivitaionField,
  handleRemoveReward,
  setTranslationLocally,
} = bonusesSlice.actions;
